import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { Button, Modal, Space } from 'antd';
import { Link } from 'react-router-dom';
import moment from 'moment';

import {
  AppPageHeader,
  DeleteIcon,
  InfiniteTable,
  TabContentWrapper,
} from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { Notification } from '@/core/domain/notification/Notification/Notification.types';
import {
  useNotificationDelete,
  useNotificationMarkRead,
  useNotificationsByUserId,
} from '@/core/domain/notification/Notification/Notification.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { ColumnsType } from 'antd/lib/table';
import CheckIcon from '@/components/_common/CheckIcon';
import { useState } from 'react';
import { ListNotificationsQuery } from '@/core/domain/notification/Message/Message.types';
import NotificationExtraFilters from '@/components/Notification/NotificationExtraFilters';
import { useDebounce } from '@/hooks';

const NotificationsPage = ({ route }: PageProps) => {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<ListNotificationsQuery>({
    order: 'DESC',
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useNotificationsByUserId({ order: 'DESC', ...queryDebounce });

  const notifications = flatInfiniteData(data);

  const removeMutation = useNotificationDelete();
  const readMutation = useNotificationMarkRead();

  const handleRemoveNotificationClick = (notificationId: number) => {
    Modal.confirm({
      title: 'Remoção de notificação',
      content:
        'Tem certeza de que deseja prosseguir com a remoção? Essa ação é irreversível.',
      onOk: async () => {
        await removeMutation.mutateAsync(notificationId);
      },
      okText: 'Remover',
      okType: 'danger',
    });
  };

  const columns: ColumnsType<Notification> = [
    {
      dataIndex: ['message', 'title'],
      title: intl.formatMessage({ id: 'notifications.table.title' }),
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      dataIndex: ['message', 'detail'],
      title: intl.formatMessage({ id: 'notifications.table.message' }),
      ellipsis: true,
      width: COLUMNS_WIDTH.default,
    },
    {
      dataIndex: ['message', 'dateTime'],
      title: intl.formatMessage({ id: 'notifications.table.DateAndTime' }),
      render: (value) =>
        value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
      width: COLUMNS_WIDTH.date,
      ellipsis: true,
    },
    {
      title: intl.formatMessage({ id: 'notifications.table.actions' }),
      align: 'right',
      fixed: 'right',
      width: 90,
      render: (_, row) => (
        <Space>
          <CheckIcon
            onClick={async () => await readMutation.mutateAsync(row.id)}
            disabled={row.readed}
          />

          <DeleteIcon onClick={() => handleRemoveNotificationClick(row.id)} />
        </Space>
      ),
    },
  ];
  return (
    <TabContentWrapper
      permissions={route.permissions}
      title={route.name}
      extra={
        <Space>
          <Link to="/app/notifications/create">
            <Button type="primary">
              {intl.formatMessage({
                id: 'notifications.list.createNotifications',
              })}
            </Button>
          </Link>
        </Space>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, title: value }))
        }
        searchValue={queryParams.title}
        headerActions={({ isShowingFilters }) =>
          isShowingFilters && (
            <Button
              type="primary"
              onClick={() => setQueryParams({ order: 'DESC' })}
            >
              {intl.formatMessage({ id: 'commons.clear.filters' })}
            </Button>
          )
        }
        extraFilters={
          <NotificationExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />

      <InfiniteTable<Notification>
        columns={columns}
        dataSource={notifications}
        loading={isFetching}
        rowKey={(row) => row.id}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        scroll={{ x: getTableScrollX(columns) }}
      />
    </TabContentWrapper>
  );
};

export default NotificationsPage;
